import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ChevronLeft, Download, RefreshCw } from "lucide-react";
import { Ornament } from "../components/Ornament";
import { HoldingSignups } from "../components/HoldingSignups";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SOURCE_LABELS = {
  "story-week-landing": "Story Week landing",
  "book-club": "Book club",
  "landing": "Home page",
  "founding_beta": "Founding beta",
  "unknown": "Unknown",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function csvEscape(v) {
  const s = String(v == null ? "" : v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(entries) {
  const header = ["created_at", "name", "email", "city", "role", "source"];
  const lines = [header.join(",")];
  for (const e of entries) {
    lines.push([
      e.created_at, e.name, e.email, e.city, e.role, e.source,
    ].map(csvEscape).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `living-book-waitlist-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminWaitlist() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await api.get("/admin/waitlist");
      setData(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Couldn't load the waitlist. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const filtered = useMemo(() => {
    if (!data?.entries) return [];
    const q = query.trim().toLowerCase();
    return data.entries.filter((e) => {
      if (sourceFilter && (e.source || "unknown") !== sourceFilter) return false;
      if (!q) return true;
      return (
        (e.email || "").toLowerCase().includes(q) ||
        (e.name || "").toLowerCase().includes(q) ||
        (e.city || "").toLowerCase().includes(q)
      );
    });
  }, [data, query, sourceFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center paper" data-testid="admin-waitlist-loading">
        <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>
          Loading…
        </p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen paper flex items-center justify-center px-6" data-testid="admin-waitlist-forbidden">
        <div className="max-w-md text-center">
          <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.25em]">ADMINS ONLY</p>
          <h1 className="text-3xl mt-4 mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            This page is for the team.
          </h1>
          <p className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.7 }}>
            If you think you should have access, please email hello@thelivingbook.io.
          </p>
          <Link to="/" className="mt-8 inline-block label-ui text-[12px] text-[#8C8278] hover:text-[#111D33]">
            Back to The Living Book
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen paper" data-testid="admin-waitlist-page">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between hairline-cream border-b">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="admin-waitlist-back">
          <ChevronLeft size={16} aria-hidden="true" />
          <span className="label-ui text-[12px]">Back to dashboard</span>
        </Link>
        <span className="label-ui text-[11px] text-[#B8973A] tracking-[0.25em]">ADMIN &middot; WAITLIST</span>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Waitlist
          </h1>
          <p className="mt-2 text-[#8C8278] italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
            Everyone who has raised their hand for The Living Book.
          </p>
        </div>

        <HoldingSignups />

        {err && (
          <div className="mb-6 p-4" style={{ background: "#FDECEC", border: "1px solid #E8B4B4", borderRadius: 4 }} role="alert" data-testid="admin-waitlist-error">
            <p className="text-[#8A1F1F]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>{err}</p>
          </div>
        )}

        {data && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-waitlist-summary">
              <div className="p-5 bg-white hairline-cream" style={{ borderRadius: 4 }}>
                <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.25em]">TOTAL</p>
                <p className="mt-2 text-3xl text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{data.total}</p>
              </div>
              {Object.entries(data.by_source).slice(0, 3).map(([src, count]) => (
                <div key={src} className="p-5 bg-white hairline-cream" style={{ borderRadius: 4 }}>
                  <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.25em]">{(SOURCE_LABELS[src] || src).toUpperCase()}</p>
                  <p className="mt-2 text-3xl text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{count}</p>
                </div>
              ))}
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="admin-waitlist-controls">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, or city…"
                className="flex-1 px-4 py-3 bg-white hairline-cream"
                style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "15px" }}
                data-testid="admin-waitlist-search"
              />
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-4 py-3 bg-white hairline-cream"
                style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "15px" }}
                data-testid="admin-waitlist-source-filter"
              >
                <option value="">All sources</option>
                {Object.keys(data.by_source).map((s) => (
                  <option key={s} value={s}>{SOURCE_LABELS[s] || s} ({data.by_source[s]})</option>
                ))}
              </select>
              <button
                type="button"
                onClick={load}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-white hairline-cream text-[#1B2A4A] hover:bg-[#F0EAE0] transition-colors"
                style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "14px", minHeight: 48 }}
                data-testid="admin-waitlist-refresh"
              >
                <RefreshCw size={16} aria-hidden="true" />
                {busy ? "Loading…" : "Refresh"}
              </button>
              <button
                type="button"
                onClick={() => downloadCsv(filtered)}
                disabled={!filtered.length}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#1B2A4A] text-[#E8D5A3] hover:bg-[#111D33] transition-colors disabled:opacity-40"
                style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "14px", minHeight: 48 }}
                data-testid="admin-waitlist-export"
              >
                <Download size={16} aria-hidden="true" />
                Export CSV ({filtered.length})
              </button>
            </div>

            {/* Table */}
            <div className="bg-white hairline-cream overflow-hidden" style={{ borderRadius: 4 }}>
              {filtered.length === 0 ? (
                <div className="p-10 text-center" data-testid="admin-waitlist-empty">
                  <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
                    {data.total === 0 ? "No one on the waitlist yet." : "No entries match this filter."}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left" data-testid="admin-waitlist-table">
                    <thead>
                      <tr style={{ background: "#F7F0E0" }}>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">DATE</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">NAME</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">EMAIL</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">CITY</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">ROLE</th>
                        <th className="px-4 py-3 label-ui text-[10px] text-[#8C8278] tracking-[0.2em]">SOURCE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((e, i) => (
                        <tr key={e.id || i} className="hairline-cream border-t" data-testid={`admin-waitlist-row-${i}`}>
                          <td className="px-4 py-3 text-sm text-[#8C8278]" style={{ fontFamily: "Georgia, serif" }}>{formatDate(e.created_at)}</td>
                          <td className="px-4 py-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>{e.name || <span className="text-[#B0A79A] italic">—</span>}</td>
                          <td className="px-4 py-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>
                            <a href={`mailto:${e.email}`} className="hover:underline text-[#B8973A]">{e.email}</a>
                          </td>
                          <td className="px-4 py-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>{e.city || <span className="text-[#B0A79A] italic">—</span>}</td>
                          <td className="px-4 py-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>{e.role || <span className="text-[#B0A79A] italic">—</span>}</td>
                          <td className="px-4 py-3">
                            <span className="inline-block px-2 py-1 label-ui text-[10px] tracking-[0.15em]" style={{ background: "#F7F0E0", color: "#8C6A20", borderRadius: 3 }}>
                              {SOURCE_LABELS[e.source] || e.source || "unknown"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <footer className="py-8 text-center">
        <Ornament color="#B8973A" />
      </footer>
    </div>
  );
}
